/**
 * VA-27781 — end a dead session cleanly instead of stranding the user.
 *
 * Before this, an expired access token left people on a page that rendered
 * normally while every request behind it failed:
 *
 *   - `PrivateRoute` only checked that a token EXISTED. An expired token is still
 *     a non-empty string, so the guard passed and the protected page mounted.
 *   - `refreshAccessToken()` runs once on app load and cannot rescue an expired
 *     session anyway: IAM's refresh endpoint authenticates the request with the
 *     CURRENT access token, so once that has expired the refresh is itself
 *     rejected 401. Verified against dev IAM.
 *   - Nothing anywhere reacted to a 401.
 *
 * So the user sat looking at empty widgets with no explanation and no way back to
 * login. Two checks fix it: expiry is judged at route entry, and any 401 on a
 * request that carried our bearer token ends the session there and then.
 *
 * The pure helpers live here so they are testable without a browser.
 */
/** Keys cleared when a session ends. `access`/`refresh` are the session itself. */
const SESSION_KEYS = ['access', 'refresh', 'crm-access-token'];
/**
 * Keys cleared when we are only retiring the ACCESS token, keeping the refresh
 * token so the session can be renewed silently.
 *
 * An expired access token is not a logout -- it is the normal, expected end of
 * a token's life, and the refresh token exists precisely to survive it. The
 * previous code cleared `refresh` too, which deleted the one credential needed
 * to recover and turned every token expiry into a forced re-login.
 */
const ACCESS_ONLY_KEYS = ['access'];
/** Treat a token as dead slightly early, so a request in flight cannot outlive it. */
const EXPIRY_SKEW_SEC = 30;
/** Endpoints where a 401 is an ANSWER, not an expired session. */
const AUTH_ENDPOINT_MARKERS = [
    'auth/refresh-token',
    'auth/login',
    'auth/signup',
    'auth/forgot-password',
    'auth/reset-password',
    'auth/verify-otp',
    'auth/send-otp',
    'login/',
    'signup/',
];
/** Strip the JSON quoting these values are stored with. */
export const unquote = (raw) => (raw || '').replace(/^"|"$/g, '');
/**
 * Seconds-since-epoch this JWT expires, or null when that cannot be determined.
 *
 * Returning null is deliberately NOT "expired": an opaque or unfamiliar token
 * must keep working exactly as it does today. Logging everyone out because a
 * token shape changed would be a far worse bug than the one this fixes — the 401
 * interceptor is the backstop for those.
 */
export const getJwtExpiry = (token) => {
    const raw = unquote(token);
    if (!raw)
        return null;
    const parts = raw.split('.');
    if (parts.length < 2)
        return null;
    try {
        const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
        const decoded = JSON.parse(decodeURIComponent(atob(padded)
            .split('')
            .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')));
        const exp = Number(decoded === null || decoded === void 0 ? void 0 : decoded.exp);
        return Number.isFinite(exp) ? exp : null;
    }
    catch (_a) {
        return null;
    }
};
/** True only when we can PROVE the token is past its expiry (minus skew). */
export const isTokenExpired = (token, nowMs = Date.now()) => {
    const exp = getJwtExpiry(token);
    if (exp === null)
        return false; // unknown shape → leave it alone
    return exp * 1000 <= nowMs + EXPIRY_SKEW_SEC * 1000;
};
/** Is there a usable session right now? */
export const hasLiveSession = (token, nowMs = Date.now()) => Boolean(unquote(token)) && !isTokenExpired(token, nowMs);
export const shouldEndSessionOn401 = (args) => {
    if (args.status !== 401 || !args.hadBearer)
        return false;
    const url = (args.url || '').toLowerCase();
    return !AUTH_ENDPOINT_MARKERS.some((marker) => url.includes(marker));
};
/** Where to send someone back to after they log in again. */
export const buildLoginRedirect = (pathname, search = '', hash = '') => {
    const here = `${pathname}${search}${hash}`;
    // Never bounce /login back to itself, and never carry a login URL as `next`.
    if (pathname.startsWith('/login'))
        return '/login';
    return `/login?next=${encodeURIComponent(here)}`;
};
export const clearAccessTokenOnly = () => {
    try {
        ACCESS_ONLY_KEYS.forEach((key) => localStorage.removeItem(key));
    }
    catch (_a) {
        // storage unavailable (private mode) — nothing to clear
    }
    try {
        // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
        require('./auth-token-store').clearAccessToken();
    }
    catch (_b) {
        /* non-browser / module unavailable */
    }
};
export const clearSessionStorage = () => {
    try {
        SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
    }
    catch (_a) {
        // storage unavailable (private mode) — nothing to clear
    }
    // VA-27781: the tokens live in cookies now, so clearing localStorage alone would
    // leave the real session intact and "logout" would not log anyone out. Imported
    // lazily to keep this module free of DOM dependencies for its unit tests.
    try {
        // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
        require('./auth-token-store').clearTokens();
    }
    catch (_b) {
        /* non-browser / module unavailable */
    }
};
